import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, FlaskConical, Play, QrCode, RefreshCw, Smartphone } from 'lucide-react';
import { ApiClientError, Spinner } from '@riskpay/ui-components';
import { api } from '../lib/client';

/* ==========================================================================
 *  UPI intent tester
 *
 *  Runs the real intent API against this merchant's account and shows what
 *  comes back — the deep link, the per-app links and the QR image, exactly as
 *  a customer would receive them.
 *
 *  It exists because "does the intent API work for me?" used to be answerable
 *  only from a terminal with a secret key pasted into it, and a developer who
 *  cannot see the thing work reasonably concludes it does not. Always sandbox:
 *  the endpoint forces test mode regardless of the portal toggle.
 * ========================================================================== */

interface IntentResponse {
  payment_id: string;
  amount: string;
  amount_display: string;
  intent_uri: string;
  apps: { key: string; name: string; link: string }[];
  qr: { url: string; svg_url: string; payload: string };
  payee_vpa: string | null;
  routed: boolean;
  route?: string | null;
}

export function IntentTester() {
  const [amount, setAmount] = useState('499.00');
  const [result, setResult] = useState<IntentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [payment, setPayment] = useState<{ status: string; upi?: { rrn: string | null } | null } | null>(null);

  // The simulated payer approves after a few seconds, so a static QR would sit
  // there looking stuck. Polling turns the tester into the whole loop:
  // intent issued → payer approves → captured, with the RRN that came back.
  useEffect(() => {
    if (!result) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const p = await api.get<{ status: string; upi?: { rrn: string | null } | null }>(
          `/developer/test-intent/${result.payment_id}`,
        );
        if (!cancelled) setPayment(p);
        return ['success', 'failed', 'expired', 'cancelled'].includes(p.status);
      } catch {
        return false;
      }
    };
    void tick();
    const timer = window.setInterval(async () => {
      if (await tick()) window.clearInterval(timer);
    }, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [result]);

  const settled = payment && ['success', 'failed', 'expired', 'cancelled'].includes(payment.status);

  const run = useMutation({
    mutationFn: () =>
      api.post<IntentResponse>('/developer/test-intent', {
        amount: amount.trim(),
        note: 'Portal test intent',
      }),
    onSuccess: (data) => {
      setError(null);
      setPayment(null);
      setResult(data);
    },
    onError: (err) => {
      setResult(null);
      setError(err instanceof ApiClientError ? err.message : 'The intent could not be created.');
    },
  });

  return (
    <section>
      <div className="mb-4 border-b border-line pb-3">
        <h2 className="flex items-center gap-2.5 text-lg font-semibold tracking-tight">
          Try the UPI intent API
          <span className="badge badge-warning">
            <FlaskConical className="h-3 w-3" aria-hidden="true" />
            Sandbox
          </span>
        </h2>
        <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-content-muted">
          Calls <code className="numeral text-xs text-content-subtle">POST /v1/upi/intent</code> for your account and
          shows exactly what a customer would receive. Always test mode — nothing here can charge anyone.
        </p>
      </div>

      <div className="rounded-lg border border-line bg-surface-raised p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <label htmlFor="intent-amount" className="label">
              Amount (₹)
            </label>
            <input
              id="intent-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              className="input numeral"
            />
          </div>
          <button type="button" onClick={() => run.mutate()} disabled={run.isPending} className="btn btn-primary">
            {run.isPending ? <Spinner /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {result ? 'Run again' : 'Create test intent'}
          </button>
          {result && (
            <button
              type="button"
              onClick={() => {
                setResult(null);
                setPayment(null);
                setError(null);
              }}
              className="btn btn-ghost"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Clear
            </button>
          )}
        </div>

        {error && (
          <p role="alert" className="mt-4 rounded-md border border-danger-ring bg-danger-soft px-3.5 py-3 text-sm text-danger-text">
            {error}
          </p>
        )}

        {result && (
          <div className="mt-6 grid gap-6 border-t border-line pt-6 lg:grid-cols-[auto_minmax(0,1fr)]">
            {/* The QR is fetched from the API, so seeing it here proves the
                whole path — routing, upstream, storage and rendering. */}
            <figure className="shrink-0">
              {settled ? (
                <div className="flex h-[168px] w-[168px] flex-col items-center justify-center rounded-lg border border-line bg-surface-inset text-center">
                  <span
                    className={`flex h-10 w-10 items-center justify-center rounded-full ${
                      payment?.status === 'success' ? 'bg-brand-500' : 'bg-surface-overlay'
                    }`}
                  >
                    <Check
                      className={`h-5 w-5 ${payment?.status === 'success' ? 'text-[#04120C]' : 'text-content-faint'}`}
                      strokeWidth={3}
                      aria-hidden="true"
                    />
                  </span>
                  <p className="mt-3 text-sm font-medium text-content">
                    {payment?.status === 'success' ? 'Captured' : (payment?.status ?? '')}
                  </p>
                  {payment?.upi?.rrn && <p className="numeral mt-1 text-2xs text-content-faint">RRN {payment.upi.rrn}</p>}
                </div>
              ) : (
                <img
                  src={result.qr.url}
                  alt={`QR code for ${result.payment_id}`}
                  width={168}
                  height={168}
                  className="rounded-lg bg-white p-2"
                />
              )}
              <figcaption className="mt-2 flex items-center gap-1.5 text-2xs text-content-faint">
                <QrCode className="h-3 w-3" aria-hidden="true" />
                {settled ? 'The sandbox payer has responded' : 'Scannable with any UPI app'}
              </figcaption>
            </figure>

            <div className="min-w-0">
              <dl className="grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="w-24 shrink-0 text-content-faint">Payment</dt>
                  <dd className="numeral truncate text-content">{result.payment_id}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-24 shrink-0 text-content-faint">Amount</dt>
                  <dd className="numeral text-content">{result.amount_display}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-24 shrink-0 text-content-faint">Payee VPA</dt>
                  <dd className="numeral truncate text-content">{result.payee_vpa ?? '—'}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-24 shrink-0 text-content-faint">Status</dt>
                  <dd>
                    <span
                      className={`badge ${
                        payment?.status === 'success'
                          ? 'badge-success'
                          : payment && payment.status !== 'pending' && payment.status !== 'initiated'
                            ? 'badge-danger'
                            : 'badge-warning'
                      }`}
                    >
                      {payment?.status ?? 'awaiting payer'}
                    </span>
                  </dd>
                </div>
                <div className="flex gap-2">
                  <dt className="w-24 shrink-0 text-content-faint">Routed</dt>
                  <dd className="text-content">
                    {result.routed ? (
                      <span className="badge badge-success">via {result.route ?? 'a provider'}</span>
                    ) : (
                      <span className="badge badge-neutral">generated locally</span>
                    )}
                  </dd>
                </div>
              </dl>

              <p className="mt-5 flex items-center gap-2 text-2xs uppercase tracking-label text-content-subtle">
                <Smartphone className="h-3 w-3" aria-hidden="true" />
                App links · {result.apps.length}
              </p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {result.apps.map((app) => (
                  <a
                    key={app.key}
                    href={app.link}
                    className="btn btn-secondary btn-sm"
                    // These open a native app; on a desktop they simply do nothing.
                    title={app.link}
                  >
                    {app.name}
                  </a>
                ))}
              </div>

              <p className="mt-5 text-2xs uppercase tracking-label text-content-subtle">Intent URI</p>
              <code className="numeral mt-2 block break-all rounded-md border border-line bg-surface-inset px-3 py-2.5 text-2xs leading-relaxed text-content-muted">
                {result.intent_uri}
              </code>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
